<template>
  <div>
    <VehicleOneColLayout
      v-if="!wide"
      :session="session"
      :adminPortalData="adminPortalData"
      :title="title"
      :quote="quote"
      :salsacCosts="salsacCosts"
      :carHireType="carHireType"
      :driverProfile="session.driverProfile"
      :totalEstimatedElectricCostPerMonth="totalEstimatedElectricCostPerMonth"
      :showAcceptanceForm="showAcceptanceForm"
    />
    <VehicleTwoColLayout
      v-if="wide"
      :session="session"
      :adminPortalData="adminPortalData"
      :title="title"
      :quote="quote"
      :salsacCosts="salsacCosts"
      :carHireType="carHireType"
      :driverProfile="session.driverProfile"
      :totalEstimatedElectricCostPerMonth="totalEstimatedElectricCostPerMonth"
      :showAcceptanceForm="showAcceptanceForm"
    />
  </div>
</template>

<script>
import VehicleDetails from "./vehicle-details.vue";
import VehicleCosts from "./vehicle-costs.vue";
import {EventBus} from '@/event-bus.js';
import Vehicle from "../../helpers/vehicle";
import VehicleSpecs from "./vehicle-specs.vue";
import AcceptanceForm from "./acceptance-form.vue";
import VehicleOneColLayout from "./vehicle-one-col-layout.vue";
import VehicleTwoColLayout from "./vehicle-two-col-layout.vue";

export default {
  name: "vehicle",
  components: {VehicleTwoColLayout, VehicleOneColLayout, AcceptanceForm, VehicleSpecs, VehicleCosts, VehicleDetails },
  props: [
    'session',
    'quote',
    'adminPortalData',
    'title',
    'showAcceptanceForm',
    'wide',
  ],

  data() {
    return {
      costsForSalarySacrificeQuote: null,
    }
  },

  watch: {
    quote: {
      handler: function (newQuote, oldQuote) {
        if (newQuote.scheme === 'salsac' && newQuote !== oldQuote) {
          this.costsForSalarySacrificeQuote = null;
          this.getCostsForSalarySacrificeQuote();
        }
      },
      deep: true
    }
  },

  computed: {
    carHireType() {
      this.session.driverProfile['BCH'] ? 'BCH' : 'PCH';
    },

    batterySizeKwh() {
      return Vehicle.getBatterySizeFromDerivative(this.quote.derivative);
    },

    wLTPRangeMiles() {
      return Vehicle.getBatteryRangeFromDerivative(this.quote.derivative);
    },

    salsacCosts() {
      if (!this.costsForSalarySacrificeQuote && this.quote.scheme === 'salsac') {
        this.getCostsForSalarySacrificeQuote();
      }

      return this.costsForSalarySacrificeQuote;
    },

    averagePencePerMile() {
      return Vehicle.averagePencePerMile(this.totalMonthlyCost, this.milesPerCalendarMonth());
    },

    totalEstimatedElectricCostPerMonth() {
      const milesPerCalendarMonth = this.milesPerCalendarMonth();
      const ppm = this.totalMonthlyCost / milesPerCalendarMonth;

      return Number((ppm * milesPerCalendarMonth) / 100).toFixed(2);
    },

    totalMonthlyCost() {
      const milesPerCalendarMonth = this.milesPerCalendarMonth();
      const milesPerKWh = this.wLTPRangeMiles / this.batterySizeKwh

      const chargingProfile = this.getChargingProfile();

      const homeChargingStandardMonthlyMiles = chargingProfile.homeChargingStandardPercentage * milesPerCalendarMonth;
      const publicChargingStandardMonthlyMiles = chargingProfile.publicChargingStandardPercentage * milesPerCalendarMonth
      const publicChargingFastMonthlyMiles = chargingProfile.publicChargingFastPercentage * milesPerCalendarMonth;
      const publicChargingFreeMonthlyMiles = chargingProfile.publicChargingFreePercentage * milesPerCalendarMonth;

      const homeChargingStandardTotalKWhForMonthlyMiles = homeChargingStandardMonthlyMiles / milesPerKWh;
      const publicChargingStandardTotalKWhForMonthlyMiles = publicChargingStandardMonthlyMiles / milesPerKWh;
      const publicChargingFastTotalKWhForMonthlyMiles = publicChargingFastMonthlyMiles / milesPerKWh;
      const publicChargingFreeTotalKWhForMonthlyMiles = publicChargingFreeMonthlyMiles / milesPerKWh;
      const homeChargingStandardMonthlyCost = (+this.adminPortalData.content.global.ev_charging_home / 100) * homeChargingStandardTotalKWhForMonthlyMiles;
      const publicChargingStandardMonthlyCost = (+this.adminPortalData.content.global.ev_charging_public_standard / 100) * publicChargingStandardTotalKWhForMonthlyMiles;
      const publicChargingFastMonthlyCost = (+this.adminPortalData.content.global.ev_charging_public_fast / 100) * publicChargingFastTotalKWhForMonthlyMiles;
      const publicChargingFreeMonthlyCost = (+this.adminPortalData.content.global.ev_charging_public_free / 100) * publicChargingFreeTotalKWhForMonthlyMiles;
      const totalMonthlyCost = homeChargingStandardMonthlyCost + publicChargingStandardMonthlyCost + publicChargingFastMonthlyCost + publicChargingFreeMonthlyCost;

      return Number(totalMonthlyCost).toFixed(2);
    },
  },

  methods: {
    milesPerCalendarMonth() {
      return this.quote.definition.plan.annualDistance / 12;
    },

    async getCostsForSalarySacrificeQuote() {
      try {
        const response = await this.$http.get('/api/get-salary-sacrifice-costs/' + this.quote.quoteID);

        if (response.data.status !== 200) {
          this.$notify({
            group: 'notify',
            type: 'error',
            title: 'Error',
            text: response?.data?.error?.message
          });
        } else {
          this.costsForSalarySacrificeQuote = response.data.data;
        }


      } catch(err) {
        let response = err?.response?.data;

        if (response) {
          this.$notify({
            group: 'notify',
            type: 'error',
            title: 'Error',
            text: response.message
          });
        }

        this.costsForSalarySacrificeQuote = 'NOT_FOUND';
      }

      EventBus.$emit('loading', false);
    },

    async getChargingProfile() {
      const profile = {
        homeChargingStandardPercentage: 0,
        publicChargingStandardPercentage: 0,
        publicChargingFastPercentage: 0,
        publicChargingFreePercentage: 0
      }

      const response = await this.$http.get('/api/get-charging-profile');

      if (response.status === 200) {
        profile.homeChargingStandardPercentage = response.data.data.home_charging_standard_percentage;
        profile.publicChargingStandardPercentage = response.data.data.public_charging_standard_percentage;
        profile.publicChargingFastPercentage = response.data.data.public_charging_fast_percentage;
        profile.publicChargingFreePercentage = response.data.data.public_charging_free_percentage;
      }

      return profile;
    },
  }

}
</script>

<style scoped>

</style>
